import React, { useState, useEffect, useRef } from 'react'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import { runQuery } from 'js/dato'
import NutritionFooter from './components/NutritionFooter'
import NutritionHeader from './components/NutritionHeader'
import { useLocation, useParams } from 'react-router-dom'
import LSButton from 'lifesum-web/src/js/components/LSButton'
import Teaser, {
  TeaserFragment,
} from 'js/pages/NutritionExplained/components/Teaser'
import { Title } from 'react-head'
import { AllStories, AllStories_allNutritionArticles } from './types/AllStories'

const query = gql`
  query AllStories($skip: IntType!, $archived: BooleanType) {
    meta: _allNutritionArticlesMeta(filter: { archived: { eq: $archived } }) {
      count
    }
    allNutritionArticles(
      first: 10
      skip: $skip
      orderBy: _createdAt_DESC
      filter: { archived: { eq: $archived } }
    ) {
      ...TeaserFragment
    }
  }
  ${TeaserFragment}
`

type NutritionArticlesAllProps = {
  initialStories: AllStories_allNutritionArticles[]
  count: number
  archive: boolean
}

function NutritionArticlesAll({
  initialStories,
  count,
  archive,
}: NutritionArticlesAllProps) {
  const [loading, setLoading] = useState(false)
  const [allStories, setAllStories] = useState(initialStories)
  const { pageNumber } = useParams()
  const initialPage = useRef(Number(pageNumber))
  const [page, setPage] = useState(Number(pageNumber))

  useEffect(() => {
    if (page === initialPage.current) return
    setLoading(true)
    runQuery(query, {
      skip: (page - 1) * 10,
      archived: archive,
    }).then(({ allNutritionArticles }) => {
      setAllStories((allStories) => allStories.concat(allNutritionArticles))
      setLoading(false)
    })
  }, [page, archive])

  return (
    <div className="bg-Bg">
      <Title>{archive ? 'Archive' : 'Nutrition stories'} - Lifesum</Title>
      <NutritionHeader />
      <div
        className="bg-BgSub px-16 pb-80 pt-48 text-center lg:pb-128 lg:pt-72"
        style={{
          backgroundImage:
            'url(' +
            'https://www.datocms-assets.com/23496/1667211510-lifesum_gradient_green.png' +
            ')',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <h1 className="type-large-fat text-white">
          {archive ? 'Archive' : 'All stories'}
        </h1>
        <p className="mx-auto mt-12 max-w-576 leading-body type-17 md:mt-16 md:type-24 lg:type-28">
          Page {pageNumber}
        </p>
      </div>
      <div className="o-width-limiter py-48 lg:py-64 xl:py-72">
        <div className="flex-wrap md:flex">
          {allStories.map(({ excerpt, title, slug, ...story }) => {
            if (!title || !slug) {
              throw new Error(
                `typeguard: missing required allStories prop: ${JSON.stringify({
                  title,
                  slug,
                })}`,
              )
            }
            return (
              <div key={story.id} className="mb-32 lg:mb-40 lg:w-1/2 xl:mb-64">
                <div className="md:mx-16 lg:mx-24 xl:mx-32">
                  <Teaser
                    excerpt={excerpt as string}
                    title={title}
                    slug={slug}
                    {...story}
                  />
                </div>
              </div>
            )
          })}
        </div>
        <div className="text-center">
          {allStories.length < count && (
            <LSButton
              link={false}
              size="small"
              variant="secondary"
              onClick={() => {
                setPage(page + 1)
                history.pushState(
                  {},
                  '',
                  `/nutrition-explained/${archive ? 'archive' : 'page'}/${
                    page + 1
                  }`,
                )
              }}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  width="14"
                  color="Type"
                  className="relative"
                  style={{ top: 1 }}
                />
              ) : (
                'Load more'
              )}
            </LSButton>
          )}
          <p className="type-nano py-[12px] text-grey">
            Showing {allStories.length} of a total of {count} stories.
          </p>
        </div>
      </div>
      <NutritionFooter />
    </div>
  )
}

type Props = {
  data: AllStories
  isLoading: boolean
  error: any
}

function NutritionArticlesAllWrapper({ data, isLoading, error }: Props) {
  const { pathname } = useLocation()

  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const { allNutritionArticles, meta } = data
  const archive = pathname.includes('archive')

  return (
    <NutritionArticlesAll
      initialStories={allNutritionArticles}
      count={meta.count}
      archive={archive}
    />
  )
}

NutritionArticlesAllWrapper.getInitialProps = ({ params, location }) => {
  const skip = Number(params.pageNumber - 1) * 10
  const archived = location.pathname.includes('archive')
  return runQuery(query, { skip, archived })
}

export default withSSR(NutritionArticlesAllWrapper)
